import React from 'react';

export type SectionVariant = 'dark' | 'light' | 'transparent' | 'darker';

export interface SectionProps {
  /**
   * Section id used as navigation anchor
   */
  id?: string;
  /**
   * Background color variant
   * - dark: primary dark background (#1a1a1a)
   * - darker: slightly darker variant (#111111)
   * - light: light background with subtle contrast (#242424)
   * - transparent: no background (inherits parent)
   */
  variant?: SectionVariant;
  /**
   * Content to render inside the section
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * ARIA label for the section
   */
  'aria-label'?: string;
  /**
   * ID of the element that labels this section
   */
  'aria-labelledby'?: string;
}

/**
 * Section wrapper component providing consistent layout structure.
 *
 * - Provides navigation anchor via `id` prop
 * - Consistent vertical padding and horizontal centering
 * - Background color variants matching the restaurant's dark/elegant theme
 *
 * Requirements: 2.1, 2.2, 10.1, 10.2, 10.3
 */
export const Section: React.FC<SectionProps> = ({
  id,
  variant = 'dark',
  children,
  className = '',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}) => {
  const variantStyles: Record<SectionVariant, string> = {
    dark: 'bg-black/70',
    darker: 'bg-black/80',
    light: 'bg-black/60',
    transparent: 'bg-transparent',
  };

  return (
    <section
      id={id}
      className={`w-full py-16 md:py-20 lg:py-24 ${variantStyles[variant]} ${className}`}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      {/* Centered content container with responsive horizontal padding */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {children}
      </div>
    </section>
  );
};
